(async () => {
  const baseComponent = await import(window.theme.modules.baseComponent);

  // 420 Sale Configuration (self-deactivating after sale window)
  const SALE_420 = {
    start: new Date('2026-04-15T00:00:00-07:00'),
    end:   new Date('2026-04-23T00:00:00-07:00'),
    tiers: {
      cbd:    { '1 oz': 42, '1/4 lb': 30, '1/2 lb': 30, '1 lb': 20, '10 lb': 11 },
      trim:   { '1 lb': 15, '15 lb': 10, '100 lb': 10 },
      kief:   {}, // Pending Jacob's decision
      roller: { '7 pack': 30 }
    }
  };

  customElements.define('product-price', class extends baseComponent.default {

    render() {
      this._initCurrentVariant();
      this._initSaleContext();

      this.listenTo('variantChange', (e, data) => {
        this.currentVariant = data.variant;
        this._update(data.html);
        this._updateSubtotalFromHTML(data.html);
        this._updateSubtotal();
        this.style.display = 'block';
      });

      this.listenTo('sellingPlanChange', (e, data) => {
        this._update(data.html);
        this._updateSubtotalFromHTML(data.html);
        this._updateSubtotal();
      });

      // Listen to manual quantity change (select dropdown)
      this.closest('section')?.addEventListener('change', (e) => {
        if (e.target.matches('product-qty select[name="quantity"]')) {
          this._updateSubtotal();
        }
      });

      // Listen when quantity dropdown is re-rendered
      this.listenTo('qtyStatusUpdate', (e, data) => {
        if (this.currentVariant?.id === +data.variantId) {
          this._updateSubtotal();
        }
      });

      // Apply sale pricing on initial page load
      this._updateSubtotal();
    }

    /* -------------------------
       Initialize variant on page load
    -------------------------- */
    _initCurrentVariant() {
      const section = this.closest('section');
      if (!section) return;
      const variantsScript = section.querySelector('[data-variants-json]');
      const qtyEl = section.querySelector('product-qty');
      if (!variantsScript || !qtyEl) return;
      const variantId = +qtyEl.getAttribute('variant-id');
      if (!variantId) return;
      try {
        const variants = JSON.parse(variantsScript.textContent);
        this.currentVariant = variants.find(v => v.id === variantId) || null;
      } catch(e) { /* noop */ }
    }

    /* -------------------------
       Initialize sale context
    -------------------------- */
    _initSaleContext() {
      this._saleActive = false;
      this._isThca = false;
      this._isDiscounted = false;

      const now = new Date();
      if (now >= SALE_420.start && now < SALE_420.end) {
        this._saleActive = true;
      }

      const section = this.closest('section');
      if (!section) return;
      const pvScript = section.querySelector('[data-pv]');
      if (pvScript) {
        try {
          const pv = JSON.parse(pvScript.textContent);
          this._isThca = pv.thca === true;
          this._isDiscounted = pv.disc === true;
        } catch(e) { /* noop */ }
      }
    }

    /* -------------------------
       Get sale discount % for a variant
    -------------------------- */
    _getSaleDiscount(variant) {
      if (!this._saleActive || this._isThca || this._isDiscounted) return 0;

      const bagSize = (variant.options[variant.options.length - 1] || '').toLowerCase().replace(/\.$/, '');
      const pt = variant.pt;

      if (pt === 'trim') return SALE_420.tiers.trim[bagSize] || 0;
      if (pt === 'kief') return SALE_420.tiers.kief[bagSize] || 0;
      if (bagSize === '7 pack') return SALE_420.tiers.roller[bagSize] || 0;
      return SALE_420.tiers.cbd[bagSize] || 0;
    }

    /* -------------------------
       Format currency
    -------------------------- */
    _formatMoney(amountInDollars) {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(amountInDollars);
    }

    /* -------------------------
       Update Price (existing logic)
    -------------------------- */
    _update(html) {
      const source = html.querySelector('product-price');
      this.replaceChildren(...source.cloneNode(true).childNodes);
    };

    /* -------------------------
       Update Subtotal Markup
       (if Shopify re-rendered it)
    -------------------------- */
    _updateSubtotalFromHTML(html) {
      const source = html.querySelector('product-subtotal');
      const target = document.querySelector('product-subtotal');
      if (!source || !target) return;

      target.replaceChildren(...source.cloneNode(true).childNodes);
    }

    /* -------------------------
       Dynamic Subtotal (qty x price + sale pricing)
    -------------------------- */
    _updateSubtotal() {
      if (!this.currentVariant) return;

      const qtyWrapper = document.querySelector('product-qty');
      if (!qtyWrapper) return;

      const select = qtyWrapper.querySelector('select[name="quantity"]');
      if (!select) return;

      const qty = parseInt(select.value || 1);
      const subtotalCents = this.currentVariant.price * qty;

      const subtotalWrapper = document.querySelector('product-subtotal');
      if (!subtotalWrapper) return;

      const subtotalEl = subtotalWrapper.querySelector('.money-subtotal');
      if (!subtotalEl) return;

      // Format regular subtotal
      const amount = subtotalCents / 100;
      subtotalEl.textContent = this._formatMoney(amount);

      // 420 Sale pricing
      const discount = this._getSaleDiscount(this.currentVariant);
      const subtotalDiv = subtotalWrapper.querySelector('.productView-subtotal');

      // Recreate sale div if it was wiped by _updateSubtotalFromHTML
      let saleDiv = subtotalWrapper.querySelector('.productView-sale-subtotal');
      if (!saleDiv && this._saleActive) {
        saleDiv = document.createElement('div');
        saleDiv.className = 'productView-sale-subtotal';
        saleDiv.style.display = 'none';
        saleDiv.innerHTML =
          '<span class="sale-your-price">Your Price</span>' +
          '<strong class="money-sale-subtotal"></strong>' +
          '<span class="sale-discount-pct"></span>' +
          '<span class="sale-annotation">420 Sale · applied at checkout</span>';
        subtotalWrapper.appendChild(saleDiv);
      }

      if (discount > 0 && saleDiv) {
        const saleAmount = amount * (1 - discount / 100);
        const saleFormatted = this._formatMoney(saleAmount);

        subtotalDiv.classList.add('sale-active');
        saleDiv.style.display = '';
        saleDiv.querySelector('.money-sale-subtotal').textContent = saleFormatted;
        const pctEl = saleDiv.querySelector('.sale-discount-pct');
        if (pctEl) pctEl.textContent = discount + '% off';
      } else if (saleDiv) {
        subtotalDiv.classList.remove('sale-active');
        saleDiv.style.display = 'none';
      }
    }

  });
})();
